import { MenuItem } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import RentalFinancialProposalForm from "../forms/form";
import { PreviewProposalDto } from "@/modules/property-tracks/dtos/rental-financial-proposal";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { useEditProposal } from "@/modules/property-tracks/hooks/rental-financial-proposal/edit-proposal.hook";
import useFindProposal from "@/modules/property-tracks/hooks/rental-financial-proposal/find-proposal.hook";
import { useProposalPreview } from "@/modules/property-tracks/hooks/rental-financial-proposal/proposal-preview.hook";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";
import {
  ProposalType,
  ScopeOfFinishing,
  ScopeOfFinishingDisplay,
} from "@/modules/property-tracks/types";

interface EditProposalProps {
  uid: string;
  defaultValues: {
    customerName: string;
    compoundName: string;
    propertyTypeName: string;
    unitArea: number;
    scopeOfFinishing?: ScopeOfFinishing;
  };
}

export default function EditFinancialProposal({
  uid,
  defaultValues: {
    compoundName,
    customerName,
    propertyTypeName,
    unitArea,
    scopeOfFinishing,
  },
}: EditProposalProps) {
  const [open, setOpen] = useState(false);

  const { data, isLoading, isSuccess, isError } = useFindProposal({
    uid,
    enabled: open,
  });

  const mutation = useEditProposal();

  const { showPreview, PreviewModal } = useProposalPreview();

  const handleSubmit = (body: PreviewProposalDto) => {
    mutation.mutate(
      {
        data: {
          uid,
          ...body,
          scopeOfFinishingSnapshot: scopeOfFinishing
            ? ScopeOfFinishingDisplay[scopeOfFinishing]
            : undefined,
        },
      },
      {
        onSuccess: ({ data }) => {
          showToast({
            type: ToastType.SUCCESS,
            message: "Proposal generated successfully",
            toastId: TOAST_IDS.EDIT_FINANCIAL_PROPOSAL_SUCCESS,
          });
          showPreview(data.fileUrl);
          setOpen(false);
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.EDIT_FINANCIAL_PROPOSAL_ERROR,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (isError) {
      showToast({
        type: ToastType.ERROR,
        message: "Failed to load proposal data",
        toastId: TOAST_IDS.LOAD_FINANCIAL_PROPOSAL_ERROR,
      });
      setOpen(false);
    }
  }, [isError]);

  const defaultValues = useMemo(
    () => ({
      unitDetails: {
        clientNameSnapshot: customerName,
        compoundNameSnapshot: compoundName,
        propertyTypeNameSnapshot: propertyTypeName,
        unitAreaSnapshot: unitArea,
        finishingCost: data?.finishingCost,
        furnishingCost: data?.furnishingCost,
        pricePerMeter: data?.pricePerMeter,
      },
      financialDetails: {
        proposalType: data?.proposalType ?? ProposalType.Normal,
        finishingMonths: data?.finishingMonths,
        loanAmount: data?.loanAmount,
        loanTerm: data?.loanTerm,
        monthlyInstallment: data?.monthlyInstallment,
        annualIncreasePercent: data?.annualIncreasePercent,
        clientCostSharePercent: data?.clientCostSharePercent,
        clientRevenueSharePercent: data?.clientRevenueSharePercent,
        paymentPlan: data?.paymentPlan,
        rentalAmount: data?.rentalAmount,
        rentalYears: data?.rentalYears,
      },
      proposalDetails: {
        proposalInformation: data?.proposalInformation,
        mainSpecs: data?.mainSpecs,
        extraSpecs: data?.extraSpecs,
      },
    }),
    [data, customerName, compoundName, propertyTypeName, unitArea],
  );

  const { readonly } = useReadonlyContext();

  return (
    <>
      <MenuItem
        sx={(t) => t.nawy.typography.english.variants.body.md.regular}
        onClick={() => setOpen(true)}
        disabled={(isLoading && open) || readonly}
      >
        Edit
      </MenuItem>

      {isSuccess && (
        <RentalFinancialProposalForm
          open={open}
          setOpen={setOpen}
          isSubmitting={mutation.isPending}
          onSubmit={handleSubmit}
          defaultValues={defaultValues}
        />
      )}

      <PreviewModal />
    </>
  );
}
